import { collection, getDocs, query, where, QuerySnapshot } from 'firebase/firestore'
import { Vehicle } from '@otw/models'
import { vehicleConverter } from '@otw/models/converters'
import { db } from 'config/firebase'
import { soldDateQueryBounds, DASHBOARD_QUERY_ROOT, dashboardSoldQueryKey } from './soldDateQueryBounds'

export { soldDateQueryBounds, DASHBOARD_QUERY_ROOT, dashboardSoldQueryKey } from './soldDateQueryBounds'

export const MASTER_INVENTORY_QUERY_KEY = ['master_inventory'] as const

export const DASHBOARD_WIDGET_QUERY_ROOT = 'dashboard-widget' as const

export const DASHBOARD_ACTIVE_QUERY_KEY = [DASHBOARD_QUERY_ROOT, 'active-inventory'] as const

export const DASHBOARD_WARRANTY_QUERY_KEY = [DASHBOARD_QUERY_ROOT, 'warranty'] as const

export const DASHBOARD_PURCHASED_FOR_INVENTORY_QUERY_KEY = [
  DASHBOARD_QUERY_ROOT,
  'purchased-for-inventory',
] as const

export const DASHBOARD_READY_FOR_TRANSPORT_QUERY_KEY = [
  DASHBOARD_QUERY_ROOT,
  'ready-for-transport',
] as const

const SOLD_ELSEWHERE = 'Sold elsewhere'

const mapVehicleSnapshot = (snapshot: QuerySnapshot<Vehicle>): Vehicle[] =>
  snapshot.docs.map(docSnap => ({
    ...docSnap.data(),
    id: docSnap.id,
  }))

export const sortInventoryByDateAdded = (docs: Vehicle[]): Vehicle[] =>
  docs.sort((a, b) => {
    if (!a.date_added && !b.date_added) return 0
    if (!a.date_added) return 1
    if (!b.date_added) return -1
    return new Date(b.date_added).getTime() - new Date(a.date_added).getTime()
  })

const masterInventoryCollection = () =>
  collection(db, 'master_inventory').withConverter(vehicleConverter)

export const fetchMasterInventory = async (): Promise<Vehicle[]> => {
  const snapshot = await getDocs(masterInventoryCollection())
  return sortInventoryByDateAdded(mapVehicleSnapshot(snapshot))
}

/** Sold vehicles whose `sold_date` falls in the dashboard range (client filter still applied). */
export const fetchSoldVehiclesInRange = async (start: Date, end: Date): Promise<Vehicle[]> => {
  const { startBound, endBound } = soldDateQueryBounds(start, end)
  try {
    const snapshot = await getDocs(
      query(
        masterInventoryCollection(),
        where('status', '==', 'Sold'),
        where('sold_date', '>=', startBound),
        where('sold_date', '<=', endBound),
      ),
    )
    return sortInventoryByDateAdded(mapVehicleSnapshot(snapshot))
  } catch {
    // Composite index not live yet — still Sold-only, never the full collection.
    const snapshot = await getDocs(
      query(masterInventoryCollection(), where('status', '==', 'Sold')),
    )
    const startMs = start.getTime()
    const endMs = end.getTime()
    return sortInventoryByDateAdded(
      mapVehicleSnapshot(snapshot).filter(vehicle => {
        if (!vehicle.sold_date) return false
        const soldMs = new Date(vehicle.sold_date).getTime()
        return !Number.isNaN(soldMs) && soldMs >= startMs && soldMs <= endMs
      }),
    )
  }
}

/** Unsold / not-sold-elsewhere stock (outstanding, owned, funded, ready-to-ship). */
export const fetchActiveInventoryVehicles = async (): Promise<Vehicle[]> => {
  try {
    const snapshot = await getDocs(
      query(masterInventoryCollection(), where('status', 'not-in', ['Sold', SOLD_ELSEWHERE])),
    )
    return sortInventoryByDateAdded(mapVehicleSnapshot(snapshot))
  } catch {
    return []
  }
}

export const fetchWarrantyVehicles = async (): Promise<Vehicle[]> => {
  const snapshot = await getDocs(
    query(masterInventoryCollection(), where('warranty_purchaged', '==', true)),
  )
  return sortInventoryByDateAdded(mapVehicleSnapshot(snapshot))
}

/** Quota: trucks marked purchased-for-inventory (much smaller than all unsold). */
export const fetchPurchasedForInventoryVehicles = async (): Promise<Vehicle[]> => {
  const snapshot = await getDocs(
    query(masterInventoryCollection(), where('purchased_for_inventory', '==', true)),
  )
  return mapVehicleSnapshot(snapshot)
}

/** Ready-for-pickup units (Overview finance/shipping card), not the full unsold set. */
export const fetchVehiclesReadyForTransport = async (): Promise<Vehicle[]> => {
  try {
    const snapshot = await getDocs(
      query(
        masterInventoryCollection(),
        where('ready_for_transport.ready_for_pickup', '==', true),
      ),
    )
    return sortInventoryByDateAdded(
      mapVehicleSnapshot(snapshot).filter(vehicle => {
        const ready = vehicle.ready_for_transport
        return Boolean(ready?.ready_for_pickup && (ready.customer === true || ready.repair === true))
      }),
    )
  } catch {
    return []
  }
}
